import React, { useEffect, useRef } from "react";
import { Signal, CloudOff, RefreshCw, CloudUpload } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { audioFeedback } from "@/shared/api/audioFeedback";

export type SyncStatus = "synced" | "syncing" | "offline" | "pending";

interface SyncStatusIndicatorProps {
  status?: SyncStatus;
  pendingCount?: number;
  className?: string;
}

export const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({
  status = "synced",
  pendingCount = 0,
  className,
}) => {
  const getIcon = () => {
    switch (status) {
      case "synced":
        return (
          <div className="relative">
            <Signal className="w-6 h-6 text-white" />
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white rounded-full flex items-center justify-center">
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.5 4L3 5.5L6.5 2"
                  stroke="hsl(var(--primary))"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        );
      case "syncing":
        return (
          <RefreshCw className="w-6 h-6 text-primary animate-spin" style={{ animationDuration: "1.5s" }} />
        );
      case "offline":
        return <CloudOff className="w-6 h-6 text-[hsl(0,0%,62%)]" />;
      case "pending":
        return (
          <div className="relative animate-pulse">
            <CloudUpload className="w-6 h-6 text-primary" />
            {pendingCount > 0 && (
              <div className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-orange-500 text-white text-xs font-semibold rounded-full flex items-center justify-center px-1">
                {pendingCount}
              </div>
            )}
          </div>
        );
      default:
        return <Signal className="w-6 h-6 text-primary" />;
    }
  };

  return (
    <div
      className={cn(
        "flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-all",
        className
      )}
      aria-label={`Sync status: ${status}`}
    >
      {getIcon()}
    </div>
  );
};

export const OfflineBanner: React.FC = () => {
  const announcedRef = useRef(false);

  useEffect(() => {
    if (!announcedRef.current) {
      announcedRef.current = true;
      audioFeedback.offlineNotice();
    }
  }, []);

  return (
    <div className="w-full bg-destructive/10 border-b border-destructive/30 text-foreground px-4 py-1.5 text-sm font-medium text-center flex items-center justify-center gap-2">
      <span>📡❌</span>
      <span className="simple-hide">Offline - will sync when connected</span>
    </div>
  );
};
